import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/dates";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  CalendarClock, Plus, CheckCircle2, AlertTriangle, Clock,
  Trash2, Edit2, Receipt, Wallet, ShieldCheck, FileText, ClipboardList, FileEdit,
} from "lucide-react";

const ICONS = { Receipt, Wallet, ShieldCheck, FileText, ClipboardList, FileEdit };

const CATEGORIES = [
  { id: "tasse", label: "Tasse", color: "bg-rose-50 text-rose-700 border-rose-300" },
  { id: "adempimenti", label: "Adempimenti", color: "bg-amber-50 text-amber-700 border-amber-300" },
  { id: "polizze", label: "Polizze", color: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  { id: "utenze", label: "Utenze", color: "bg-sky-50 text-sky-700 border-sky-300" },
  { id: "manutenzione", label: "Manutenzione", color: "bg-teal-50 text-teal-700 border-teal-300" },
  { id: "digitale", label: "Digitale", color: "bg-violet-50 text-violet-700 border-violet-300" },
  { id: "altro", label: "Altro", color: "bg-slate-50 text-slate-700 border-slate-300" },
];

const RECURRINGS = [
  { id: "none", label: "Una tantum" },
  { id: "daily", label: "Giornaliera" },
  { id: "monthly", label: "Mensile" },
  { id: "monthly_on_5", label: "Mensile (il 5)" },
  { id: "monthly_on_15", label: "Mensile (il 15)" },
  { id: "monthly_on_16", label: "Mensile (il 16)" },
  { id: "bimonthly", label: "Bimestrale" },
  { id: "quarterly", label: "Trimestrale" },
  { id: "biannual", label: "Semestrale" },
  { id: "yearly", label: "Annuale" },
  { id: "biennial", label: "Biennale" },
  { id: "as_needed", label: "Al bisogno" },
];

const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
};

const statusBadge = (d) => {
  if (d.status === "paid") return { cls: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "Pagata" };
  const k = daysUntil(d.due_date);
  if (k == null) return { cls: "bg-slate-50 text-slate-700 border-slate-300", label: "—" };
  if (k < 0) return { cls: "bg-rose-50 text-rose-700 border-rose-400", label: `Scaduta da ${-k} g` };
  if (k === 0) return { cls: "bg-rose-50 text-rose-700 border-rose-300", label: "Oggi" };
  if (k <= 7) return { cls: "bg-amber-50 text-amber-700 border-amber-300", label: `Tra ${k} g` };
  if (k <= 30) return { cls: "bg-sky-50 text-sky-700 border-sky-300", label: `Tra ${k} g` };
  return { cls: "bg-slate-50 text-slate-700 border-slate-300", label: `Tra ${k} g` };
};

const EMPTY = { title: "", category: "altro", kind: "other", due_date: "", amount: "", recurring: "none", notes: "", icon: "FileText" };

export default function DeadlinesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active"); // all | active | paid
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/deadlines");
      setItems(r.data.deadlines || []);
    } catch {
      toast.error("Errore caricamento scadenze");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return items.filter((d) => {
      if (filterCat !== "all" && d.category !== filterCat) return false;
      if (filterStatus === "active" && d.status === "paid") return false;
      if (filterStatus === "paid" && d.status !== "paid") return false;
      return true;
    });
  }, [items, filterCat, filterStatus]);

  const stats = useMemo(() => {
    let overdue = 0, dueSoon = 0, paid = 0, totalAmount = 0;
    items.forEach((d) => {
      if (d.status === "paid") { paid++; return; }
      const k = daysUntil(d.due_date);
      if (k != null) {
        if (k < 0) overdue++;
        else if (k <= 30) dueSoon++;
      }
      if (d.amount) totalAmount += Number(d.amount);
    });
    return { overdue, dueSoon, paid, totalAmount, total: items.length };
  }, [items]);

  const startCreate = () => { setEditing({ ...EMPTY, due_date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const startEdit = (d) => { setEditing({ ...d, amount: d.amount ?? "" }); setOpen(true); };

  const save = async () => {
    if (!editing.title?.trim() || !editing.due_date) {
      toast.error("Titolo e scadenza obbligatori"); return;
    }
    try {
      const payload = { ...editing, amount: editing.amount === "" ? null : Number(editing.amount) };
      if (editing.id) await api.put(`/admin/deadlines/${editing.id}`, payload);
      else await api.post("/admin/deadlines", payload);
      toast.success("Scadenza salvata");
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  const togglePaid = async (d) => {
    try {
      const newStatus = d.status === "paid" ? "pending" : "paid";
      await api.put(`/admin/deadlines/${d.id}`, { status: newStatus, paid_on: newStatus === "paid" ? new Date().toISOString().slice(0, 10) : null });
      toast.success(newStatus === "paid" ? "Segnata come pagata" : "Riaperta");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa scadenza?")) return;
    try {
      await api.delete(`/admin/deadlines/${id}`);
      toast.success("Eliminata");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  return (
    <div className="space-y-5" data-testid="deadlines-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-[#AC5D45]" />Scadenze
          </h2>
          <p className="text-sm text-[#635F59] mt-0.5">
            Tutte le scadenze del sito in un solo posto: tasse, adempimenti, polizze, utenze, manutenzione.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (!confirm("Carica/aggiorna le scadenze standard di un B&B italiano? Le voci già esistenti NON verranno modificate.")) return;
              try {
                const res = await api.post("/admin/deadlines/ensure-defaults");
                toast.success(`Aggiunte ${res.data.inserted} nuove · ${res.data.skipped_existing} già presenti`);
                refresh();
              } catch (e) {
                toast.error("Errore caricamento scadenze standard");
              }
            }}
            className="border-[#AC5D45]/40 text-[#AC5D45] hover:bg-[#AC5D45]/10"
            data-testid="deadline-ensure-defaults"
          >
            <ClipboardList className="w-4 h-4 mr-1" />Scadenze standard
          </Button>
          <Button onClick={startCreate} className="bg-[#1E3A5F] hover:bg-[#15294A] text-white" data-testid="deadline-create">
            <Plus className="w-4 h-4 mr-1" />Nuova scadenza
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="deadlines-stats">
        <StatCard icon={AlertTriangle} label="Scadute" value={stats.overdue} color="rose" testId="stat-overdue" />
        <StatCard icon={Clock} label="In arrivo (30 g)" value={stats.dueSoon} color="amber" testId="stat-due-soon" />
        <StatCard icon={CheckCircle2} label="Pagate" value={stats.paid} color="emerald" testId="stat-paid" />
        <StatCard icon={Wallet} label="Importo annuo stimato" value={stats.totalAmount ? `€${stats.totalAmount.toFixed(0)}` : "—"} color="navy" testId="stat-amount" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Categoria</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="h-9 mt-1" data-testid="deadline-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Stato</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-9 mt-1" data-testid="deadline-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Da pagare / in arrivo</SelectItem>
              <SelectItem value="paid">Pagate</SelectItem>
              <SelectItem value="all">Tutte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-x-auto">
        <Table data-testid="deadlines-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[42px]"></TableHead>
              <TableHead>Titolo</TableHead>
              <TableHead className="w-[140px]">Categoria</TableHead>
              <TableHead className="w-[130px]">Scadenza</TableHead>
              <TableHead className="w-[140px]">Status</TableHead>
              <TableHead className="w-[120px]">Ricorrenza</TableHead>
              <TableHead className="w-[110px] text-right">Importo</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#635F59]">Caricamento...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#635F59]">Nessuna scadenza con questi filtri.</TableCell></TableRow>
            ) : filtered.map((d) => {
              const Icon = ICONS[d.icon] || FileText;
              const cat = CATEGORIES.find((c) => c.id === d.category) || CATEGORIES[5];
              const st = statusBadge(d);
              return (
                <TableRow key={d.id} data-testid={`deadline-${d.id}`} className={d.status === "paid" ? "opacity-60" : ""}>
                  <TableCell><Icon className="w-4 h-4 text-[#AC5D45]" /></TableCell>
                  <TableCell>
                    <div className="font-medium text-[#1E3A5F]">{d.title}</div>
                    {d.notes && <div className="text-[10px] text-[#635F59] mt-0.5">{d.notes}</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className={cat.color}>{cat.label}</Badge></TableCell>
                  <TableCell className="text-sm font-mono">{formatDate(d.due_date)}</TableCell>
                  <TableCell><Badge variant="outline" className={st.cls}>{st.label}</Badge></TableCell>
                  <TableCell className="text-xs text-[#635F59]">{RECURRINGS.find((r) => r.id === d.recurring)?.label || "—"}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-[#1E3A5F]">
                    {d.amount ? `€${Number(d.amount).toFixed(2)}` : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => togglePaid(d)}
                        title={d.status === "paid" ? "Riapri" : "Pagata"}
                        data-testid={`deadline-toggle-${d.id}`}
                        className={d.status === "paid" ? "text-amber-700 hover:bg-amber-50 h-8 w-8" : "text-emerald-700 hover:bg-emerald-50 h-8 w-8"}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => startEdit(d)} className="text-[#1E3A5F] hover:bg-slate-100 h-8 w-8" data-testid={`deadline-edit-${d.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(d.id)} className="text-rose-600 hover:bg-rose-50 h-8 w-8" data-testid={`deadline-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="deadline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1E3A5F]">
              {editing.id ? "Modifica scadenza" : "Nuova scadenza"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titolo *</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="deadline-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Categoria</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="deadline-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Ricorrenza</Label>
                <Select value={editing.recurring} onValueChange={(v) => setEditing({ ...editing, recurring: v })}>
                  <SelectTrigger data-testid="deadline-recurring"><SelectValue /></SelectTrigger>
                  <SelectContent>{RECURRINGS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data scadenza *</Label>
                <DateInputIT value={editing.due_date || ""} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} data-testid="deadline-due-date" />
              </div>
              <div>
                <Label className="text-xs">Importo (€)</Label>
                <Input type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="deadline-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Note</Label>
              <Textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} rows={3} data-testid="deadline-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="deadline-save">
              {editing.id ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, color, testId }) => {
  const colorMap = {
    rose: "text-rose-600 bg-rose-50 border-rose-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    navy: "text-[#1E3A5F] bg-[#FDFBF7] border-[#E8E2D9]",
  };
  return (
    <div className={`border rounded-xl p-4 flex items-center gap-3 ${colorMap[color]}`} data-testid={testId}>
      <Icon className="w-7 h-7" />
      <div>
        <div className="font-display text-2xl leading-none">{value}</div>
        <div className="text-[11px] mt-0.5 opacity-80">{label}</div>
      </div>
    </div>
  );
};
